import { React, useState, useContext, useEffect } from "react";

import ThemeContext from "../../lib/theme-context";
import AuthContext from "../../lib/auth-context";

import dayjs from "dayjs";
import moment from "moment";

import CustomSelect from "../select/index";
import InstrumentSelect from "../select/InstrumentSelect";
import CustomMultiTextField from "../textfields/multiRow_standard";
import VoiceToText from "../VoiceToText";
import CustomDatePicker from "../dateTimePickers/datePicker";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";

import { DataFetcher } from "../../utils/dataFetcher";
import throwMessage from "../../utils/throwMessage";

import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";

import Tooltip from "@mui/material/Tooltip";

import ConfirmDialog from "../modals/confirm-dialog";

import useWindowDimensions from "../../utils/width";

const imgStyle = {
  width: "100%",
  height: "430px",
  objectFit: "cover",
  borderRadius: "15px",
  cursor: "pointer",
};

const NewForecast = ({ open, onClose, prefilledData = null }) => {
  const auth = useContext(AuthContext);
  const theme = useContext(ThemeContext);
  const { width } = useWindowDimensions();
  const [pair, setPair] = useState("");
  const [direction, setDirection] = useState("");
  const [date, setDate] = useState(dayjs(new Date()));
  const [changeDate, setChangeDate] = useState(false);
  const [sequence, setSequence] = useState(undefined);
  const [positioning, setPositioning] = useState(undefined);
  const [grade, setGrade] = useState(undefined);
  const [forecastType, setForecastType] = useState(undefined);
  const [sections, setSections] = useState([
    { timeframeName: "", timeframeEdit: true, screenshotLink: "", description: "", dxyScreenshotLink: "" },
    { timeframeName: "", timeframeEdit: true, screenshotLink: "", description: "", dxyScreenshotLink: "" },
  ]);
  const [showDeleteIcon, setShowDeleteIcon] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [screenshotToDelete, setScreenshotToDelete] = useState(null);
  const [dxyDeleteIndex, setDxyDeleteIndex] = useState(null);

  // Helper function to check if pair is USD-related
  const isUSDRelatedPair = (pairName) => {
    if (!pairName) return false;
    const usdPairs = ['AUDUSD', 'GBPUSD', 'EURUSD', 'NZDUSD', 'USDCHF', 'USDJPY', 'USDCAD', 'XAUUSD', 'XAGUSD', 'BTCUSD', 'SPX500'];
    return usdPairs.includes(pairName);
  };

  const handleImageMouseEnter = (index) => {
    setShowDeleteIcon(index);
  };

  const handleImageMouseLeave = (index) => {
    setShowDeleteIcon(null);
  };

  const handleDeleteScreenshot = (index, isDxy = false) => {
    setScreenshotToDelete(index);
    setDxyDeleteIndex(isDxy);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteScreenshot = () => {
    if (screenshotToDelete !== null) {
      if (dxyDeleteIndex) {
        updateSection(screenshotToDelete, "dxyScreenshotLink", "");
      } else {
        updateSection(screenshotToDelete, "screenshotLink", "");
      }
      setScreenshotToDelete(null);
      setDxyDeleteIndex(null);
    }
  };

  const updateSection = (index, field, value) => {
    setSections((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addSection = () => {
    if (sections.length < 7) {
      setSections((prev) => [
        ...prev,
        { timeframeName: "", timeframeEdit: true, screenshotLink: "", description: "", dxyScreenshotLink: "" },
      ]);
    }
  };

  const removeSection = (index) => {
    if (sections.length > 2) {
      setSections((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const [formSubmitted, setFormSubmitted] = useState(false);

  // Handle pre-filled data from new entry modal
  useEffect(() => {
    if (prefilledData && open) {
      if (prefilledData.pair) setPair(prefilledData.pair);
      if (prefilledData.direction) setDirection(prefilledData.direction);
      if (prefilledData.date) setDate(dayjs(prefilledData.date));
    }
  }, [prefilledData, open]);

  // Calculate dynamic rows based on content
  const calculateRows = (text) => {
    if (!text || text.trim() === '') return 3;
    const lineCount = text.split('\n').length;
    const charCount = text.length;
    // Estimate rows based on characters (roughly 80 chars per row)
    const estimatedRows = Math.ceil(charCount / 80);
    return Math.max(3, Math.max(lineCount, estimatedRows));
  };

  const handleSave = async () => {
    const allSectionsValid = sections.every(
      (s) => s.timeframeName && s.screenshotLink
    );
    const schema = {
      active: "true",
      pair: pair,
      direction: direction,
      date: date,
      sequence: sequence,
      positioning: positioning,
      grade: grade,
      forecastType: forecastType,
      sections: sections,
      backtestingMode: auth.backtestingMode,
    };
    if (
      schema.pair &&
      schema.direction &&
      schema.date &&
      schema.sequence &&
      schema.positioning &&
      schema.grade &&
      schema.forecastType &&
      allSectionsValid
    ) {
      if (!formSubmitted);
      setFormSubmitted(true);
      const result = await DataFetcher("new-forecast", schema);
      if (result) {
        if (auth.backtestingMode) {
          auth.setForecastsBacktesting([...auth.forecastsBacktesting, result]);
        } else {
          auth.setForecasts([...auth.forecasts, result]);
        }
        throwMessage("success", "Success");
        goBack();
        setFormSubmitted(false);
      } else {
        throwMessage("error", "Something went wrong");
        setFormSubmitted(false);
      }
    } else {
      auth?.throwMessage("warning", "Missing Fields");
    }
  };
  const goBack = () => {
    onClose();
    setDate(dayjs(new Date()));
    setPair("");
    setChangeDate(false);
    setSequence("");
    setPositioning("");
    setGrade("");
    setForecastType(undefined);
    setDirection("");
    setSections([
      { timeframeName: "", timeframeEdit: true, screenshotLink: "", description: "", dxyScreenshotLink: "" },
      { timeframeName: "", timeframeEdit: true, screenshotLink: "", description: "", dxyScreenshotLink: "" },
    ]);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ maxWidth: "1920px", margin: "0 auto" }}
      BackdropProps={{ invisible: true }}
      disableBackdropClick
    >
      <>
      <Box
        sx={{
          overflowY: "scroll",
          overflowX: "hidden",
          position: "absolute",
          top: "64px",
          left: "50%",
          transform: "translateX(-50%)",
          color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
          width: "100%",
          height: "calc(100vh - 64px)",
          backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
          padding: { xs: "10px 15px 70px 15px", sm: "10px 15px 15px 15px" },
          margin: "0 auto",
        }}
      >
        <Grid
          container
          spacing={1}
          justifyContent="space-evenly"
          alignItems="center"
          sx={{ minHeight: "100%" }}
        >
          <Grid item xs={12}>
            <div
              style={{
                maxWidth: "95%",
                margin: width <= 768 ? "0.5em auto" : "1.5em auto",
                padding: width <= 768 ? "16px 20px" : "20px 32px",
                borderRadius: "16px",
                backgroundColor:
                  theme[0] === "dark"
                    ? "rgba(45, 46, 51, 0.4)"
                    : "rgba(249, 250, 251, 0.8)",
                border: `1px solid ${
                  theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                }`,
                boxShadow:
                  theme[0] === "dark"
                    ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                    : "0 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                {pair && (
                  <span
                    onClick={() => setPair("")}
                    style={{
                      fontSize: "23px",
                      fontWeight: "600",
                      letterSpacing: "0.5px",
                      cursor: "pointer",
                      color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {pair}
                  </span>
                )}

                {direction && (
                  <span
                    onClick={() => setDirection("")}
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      letterSpacing: "0.5px",
                      cursor: "pointer",
                      padding: "3px 8px",
                      borderRadius: "6px",
                      backgroundColor:
                        direction === "Short"
                          ? theme[0] === "dark"
                            ? "rgba(219, 6, 113, 0.15)"
                            : "rgba(219, 6, 113, 0.1)"
                          : direction === "Long"
                            ? theme[0] === "dark"
                              ? "rgba(38, 166, 93, 0.15)"
                              : "rgba(38, 166, 93, 0.1)"
                            : theme[0] === "dark"
                              ? "rgba(237, 158, 14, 0.15)"
                              : "rgba(237, 158, 14, 0.1)",
                      border: `2px solid ${
                        direction === "Short"
                          ? "#db0671"
                          : direction === "Long"
                            ? "#26a65d"
                            : "#ED9E0E"
                      }`,
                      color:
                        direction === "Short"
                          ? "#db0671"
                          : direction === "Long"
                            ? "#26a65d"
                            : "#ED9E0E",
                      transition: "all 0.2s ease",
                      outline: "none",
                      WebkitTapHighlightColor: "transparent",
                      userSelect: "none",
                      display: "inline-block",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    {direction}
                  </span>
                )}

                <span
                  onClick={() => {
                    setChangeDate(true);
                    setDate(dayjs(new Date()));
                  }}
                  style={{
                    fontSize: "18px",
                    fontWeight: "500",
                    letterSpacing: "0.3px",
                    cursor: "pointer",
                    color:
                      theme[0] === "dark"
                        ? "rgba(252, 252, 255, 0.7)"
                        : "rgba(26, 27, 30, 0.7)",
                    transition: "all 0.2s ease",
                  }}
                >
                  {changeDate && !pair
                    ? "New"
                    : moment(date).format("dddd Do MMM")}
                </span>

                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: "400",
                    letterSpacing: "0.3px",
                    color:
                      theme[0] === "dark"
                        ? "rgba(252, 252, 255, 0.5)"
                        : "rgba(26, 27, 30, 0.5)",
                  }}
                >
                  {forecastType ? `${forecastType} Forecast` : "Forecast"}
                </span>
              </div>

              <hr
                style={{
                  width: "100%",
                  border: "none",
                  borderTop: `1px solid ${
                    theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                  }`,
                  margin: width <= 768 ? "12px 0 10px 0" : "20px 0 16px 0",
                }}
              />

              {pair && direction && (positioning || sequence || grade || forecastType) && (
                <div
                  style={{
                    display: width < 768 ? "grid" : "flex",
                    gridTemplateColumns: width < 768 ? "1fr 1fr" : undefined,
                    flexDirection: width < 768 ? undefined : "row",
                    justifyContent: width < 768 ? "center" : "space-evenly",
                    alignItems: "center",
                    gap: width < 768 ? "12px" : "20px",
                    flexWrap: "wrap",
                  }}
                >
                  {positioning && (
                    <div
                      onClick={() => setPositioning("")}
                      style={{
                        position: "relative",
                        cursor: "pointer",
                        padding: "4px 10px",
                        borderRadius: "12px",
                        backgroundColor:
                          positioning === "Excellent Positioning"
                            ? theme[0] === "dark"
                              ? "rgba(38, 166, 93, 0.15)"
                              : "rgba(38, 166, 93, 0.1)"
                            : positioning === "Average Positioning"
                              ? theme[0] === "dark"
                                ? "rgba(237, 158, 14, 0.15)"
                                : "rgba(237, 158, 14, 0.1)"
                              : theme[0] === "dark"
                                ? "rgba(242, 54, 69, 0.15)"
                                : "rgba(242, 54, 69, 0.1)",
                        border: `2px solid ${
                          positioning === "Excellent Positioning"
                            ? "#26a65d"
                            : positioning === "Average Positioning"
                              ? "#ED9E0E"
                              : "#f23645"
                        }`,
                        transition: "all 0.3s ease",
                        minWidth: width < 768 ? "100px" : "120px",
                        maxWidth: width < 768 ? "140px" : "160px",
                        textAlign: "center",
                        outline: "none",
                        WebkitTapHighlightColor: "transparent",
                        userSelect: "none",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.02)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      <p
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          letterSpacing: "0.8px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 2px 0",
                        }}
                      >
                        POSITIONING
                      </p>
                      <p
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          letterSpacing: "0.3px",
                          margin: 0,
                          color:
                            positioning === "Excellent Positioning"
                              ? "#26a65d"
                              : positioning === "Average Positioning"
                                ? "#ED9E0E"
                                : "#f23645",
                        }}
                      >
                        {positioning.replace(" Positioning", "").toUpperCase()}
                      </p>
                    </div>
                  )}
                  {sequence && (
                    <div
                      onClick={() => setSequence("")}
                      style={{
                        position: "relative",
                        cursor: "pointer",
                        padding: "4px 10px",
                        borderRadius: "12px",
                        backgroundColor:
                          sequence === "Excellent Sequence"
                            ? theme[0] === "dark"
                              ? "rgba(38, 166, 93, 0.15)"
                              : "rgba(38, 166, 93, 0.1)"
                            : sequence === "Average Sequence"
                              ? theme[0] === "dark"
                                ? "rgba(237, 158, 14, 0.15)"
                                : "rgba(237, 158, 14, 0.1)"
                              : theme[0] === "dark"
                                ? "rgba(242, 54, 69, 0.15)"
                                : "rgba(242, 54, 69, 0.1)",
                        border: `2px solid ${
                          sequence === "Excellent Sequence"
                            ? "#26a65d"
                            : sequence === "Average Sequence"
                              ? "#ED9E0E"
                              : "#f23645"
                        }`,
                        transition: "all 0.3s ease",
                        minWidth: width < 768 ? "100px" : "120px",
                        maxWidth: width < 768 ? "140px" : "160px",
                        textAlign: "center",
                        outline: "none",
                        WebkitTapHighlightColor: "transparent",
                        userSelect: "none",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.02)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      <p
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          letterSpacing: "0.8px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 2px 0",
                        }}
                      >
                        SEQUENCE
                      </p>
                      <p
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          letterSpacing: "0.3px",
                          margin: 0,
                          color:
                            sequence === "Excellent Sequence"
                              ? "#26a65d"
                              : sequence === "Average Sequence"
                                ? "#ED9E0E"
                                : "#f23645",
                        }}
                      >
                        {sequence.replace(" Sequence", "").toUpperCase()}
                      </p>
                    </div>
                  )}
                  {grade && (
                    <div
                      onClick={() => setGrade("")}
                      style={{
                        position: "relative",
                        cursor: "pointer",
                        padding: "4px 10px",
                        borderRadius: "12px",
                        backgroundColor:
                          grade === "Low Risk"
                            ? theme[0] === "dark"
                              ? "rgba(38, 166, 93, 0.15)"
                              : "rgba(38, 166, 93, 0.1)"
                            : grade === "High Risk"
                              ? theme[0] === "dark"
                                ? "rgba(255, 165, 0, 0.15)"
                                : "rgba(255, 165, 0, 0.1)"
                            : grade === "Invalid"
                              ? theme[0] === "dark"
                                ? "rgba(242, 54, 69, 0.15)"
                                : "rgba(242, 54, 69, 0.1)"
                              : theme[0] === "dark"
                                ? "rgba(242, 54, 69, 0.15)"
                                : "rgba(242, 54, 69, 0.1)",
                        border: `2px solid ${
                          grade === "Low Risk"
                            ? "#26a65d"
                            : grade === "High Risk"
                              ? "#FFA500"
                              : grade === "Invalid"
                                ? "#f23645"
                                : "#f23645"
                        }`,
                        transition: "all 0.3s ease",
                        minWidth: width < 768 ? "100px" : "120px",
                        maxWidth: width < 768 ? "140px" : "160px",
                        textAlign: "center",
                        outline: "none",
                        WebkitTapHighlightColor: "transparent",
                        userSelect: "none",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.02)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      <p
                        style={{
                          fontSize: "8px",
                          fontWeight: "600",
                          letterSpacing: "0.8px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 2px 0",
                        }}
                      >
                        RISK GRADE
                      </p>
                      <p
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          letterSpacing: "0.3px",
                          margin: 0,
                          color:
                            grade === "Low Risk"
                              ? "#26a65d"
                              : grade === "High Risk"
                                ? "#FFA500"
                                : grade === "Invalid"
                                  ? "#f23645"
                                  : "#f23645",
                        }}
                      >
                        {grade.replace(" Risk", "").toUpperCase()}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Grid>

          {changeDate ? (
            <Grid item xs={12}>
              <div style={{ margin: width <= 768 ? "1em auto" : "0" }}>
                <div
                  style={{
                    maxWidth: "400px",
                    margin: "0 auto",
                    padding: width <= 768 ? "16px" : "20px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <p
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      letterSpacing: "0.5px",
                      color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                      margin: "0 0 12px 0",
                      textAlign: "center",
                    }}
                  >
                    SELECT DATE
                  </p>
                  <CustomDatePicker
                    size="small"
                    label="Event Date"
                    value={date}
                    onChange={(newValue) => {
                      setChangeDate(false);
                      setDate(newValue.$d);
                    }}
                  />
                </div>
              </div>
            </Grid>
          ) : null}
          {(!pair ||
            !direction ||
            changeDate ||
            !positioning ||
            !sequence ||
            !grade ||
            !forecastType) && (
            <Grid item xs={12}>
              <div
                style={{
                  display: width <= 768 ? "block" : "grid",
                  gridTemplateColumns:
                    width <= 768
                      ? "1fr"
                      : "repeat(auto-fit, minmax(350px, 1fr))",
                  gap: width <= 768 ? "0" : "20px",
                  maxWidth: "95%",
                  margin: "1em auto",
                }}
              >
                {!pair ? (
                  <div
                    style={{
                      margin: width <= 768 ? "1em auto 0.5em auto" : "0",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "400px",
                        margin: "0 auto",
                        padding: width <= 768 ? "16px" : "20px",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                          textAlign: "center",
                        }}
                      >
                        SELECT INSTRUMENT
                      </p>
                      <InstrumentSelect
                        value={pair}
                        onChange={(event) => setPair(event.target.value)}
                        size="small"
                        fullWidth
                      />
                    </div>
                  </div>
                ) : null}
                {!direction ? (
                  <div style={{ margin: width <= 768 ? "1em auto" : "0" }}>
                    <div
                      style={{
                        maxWidth: "400px",
                        margin: "0 auto",
                        padding: width <= 768 ? "16px" : "20px",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                          textAlign: "center",
                        }}
                      >
                        SELECT DIRECTION
                      </p>
                      <CustomSelect
                        size="small"
                        placeholder="Direction"
                        value={direction}
                        onChange={(e) => setDirection(e.target.value)}
                        menuItemArray={JSON.stringify([
                          { value: "Short", label: "Short" },
                          { value: "Long", label: "Long" },
                          { value: "Netural", label: "Netural" },
                        ])}
                      />
                    </div>
                  </div>
                ) : null}

                {!positioning ? (
                  <div style={{ margin: width <= 768 ? "1em auto" : "0" }}>
                    <div
                      style={{
                        maxWidth: "400px",
                        margin: "0 auto",
                        padding: width <= 768 ? "16px" : "20px",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                          textAlign: "center",
                        }}
                      >
                        POSITIONING
                      </p>
                      <CustomSelect
                        size="small"
                        placeholder="Positioning"
                        value={positioning}
                        onChange={(e) => setPositioning(e.target.value)}
                        menuItemArray={JSON.stringify([
                          { value: "Poor Positioning", label: "Poor" },
                          { value: "Average Positioning", label: "Average" },
                          {
                            value: "Excellent Positioning",
                            label: "Excellent",
                          },
                        ])}
                      />
                    </div>
                  </div>
                ) : null}
                {!sequence ? (
                  <div style={{ margin: width <= 768 ? "1em auto" : "0" }}>
                    <div
                      style={{
                        maxWidth: "400px",
                        margin: "0 auto",
                        padding: width <= 768 ? "16px" : "20px",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                          textAlign: "center",
                        }}
                      >
                        SEQUENCE
                      </p>
                      <CustomSelect
                        size="small"
                        placeholder="Sequence"
                        value={sequence}
                        onChange={(e) => setSequence(e.target.value)}
                        menuItemArray={JSON.stringify([
                          { value: "Poor Sequence", label: "Poor" },
                          { value: "Average Sequence", label: "Average" },
                          { value: "Excellent Sequence", label: "Excellent" },
                        ])}
                      />
                    </div>
                  </div>
                ) : null}
                {!grade ? (
                  <div style={{ margin: width <= 768 ? "1em auto" : "0" }}>
                    <div
                      style={{
                        maxWidth: "400px",
                        margin: "0 auto",
                        padding: width <= 768 ? "16px" : "20px",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                          textAlign: "center",
                        }}
                      >
                        RISK GRADE
                      </p>
                      <CustomSelect
                        size="small"
                        placeholder="Risk Grade"
                        value={grade}
                        onChange={(e) => setGrade(e.target.value)}
                        menuItemArray={JSON.stringify([
                          { value: "Low Risk", label: "Low Risk" },
                          { value: "High Risk", label: "High Risk" },
                          { value: "Invalid", label: "Invalid" },
                        ])}
                      />
                    </div>
                  </div>
                ) : null}
                {!forecastType ? (
                  <div style={{ margin: width <= 768 ? "1em auto" : "0" }}>
                    <div
                      style={{
                        maxWidth: "400px",
                        margin: "0 auto",
                        padding: width <= 768 ? "16px" : "20px",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                          textAlign: "center",
                        }}
                      >
                        FORECAST TYPE
                      </p>
                      <CustomSelect
                        size="small"
                        placeholder="Forecast Type"
                        value={forecastType}
                        onChange={(e) => setForecastType(e.target.value)}
                        menuItemArray={JSON.stringify([
                          { value: "Main", label: "Main" },
                          { value: "Additional", label: "Additional" },
                          { value: "Potential", label: "Potential" },
                        ])}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </Grid>
          )}

          <Grid
            container
            spacing={0}
            sx={{ maxWidth: "95%", margin: "0 auto", padding: "0" }}
          >
            {sections.map((section, index) => (
              <Grid
                key={index}
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    width: "100%",
                    minHeight: "60px",
                    margin: "0 auto",
                    paddingTop: "0.5em",
                  }}
                >
                  {section.timeframeEdit || !section.timeframeName ? (
                    <div
                      style={{
                        maxWidth: "400px",
                        margin: "0 auto 1em auto",
                        padding: width <= 768 ? "16px" : "20px",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                          textAlign: "center",
                        }}
                      >
                        SELECT TIMEFRAME
                      </p>
                      <CustomSelect
                        size="small"
                        placeholder="Timeframe"
                        value={section.timeframeName}
                        onChange={(e) => {
                          updateSection(index, "timeframeName", e.target.value);
                          updateSection(index, "timeframeEdit", false);
                        }}
                        menuItemArray={JSON.stringify([
                          { label: "5m", value: "5m" },
                          { label: "15m", value: "15m" },
                          { label: "1h", value: "1h" },
                          { label: "4h", value: "4h" },
                          { label: "Daily", value: "Daily" },
                          { label: "Weekly", value: "Weekly" },
                          { label: "Monthly", value: "Monthly" },
                        ])}
                      />
                    </div>
                  ) : (
                    <div
                      onClick={() => updateSection(index, "timeframeEdit", true)}
                      style={{
                        cursor: "pointer",
                        padding: "10px 16px",
                        margin: "0.5em auto 0.3em auto",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `2px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                        textAlign: "center",
                        maxWidth: "160px",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.02)";
                        e.currentTarget.style.borderColor =
                          theme[0] === "dark" ? "#9ca3af" : "#6b7280";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.borderColor =
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb";
                      }}
                    >
                      <p
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                          margin: 0,
                        }}
                      >
                        {section.timeframeName}
                      </p>
                    </div>
                  )}
                </div>
                {section.screenshotLink ? (
                  section.screenshotLink.startsWith(
                    "https://www.tradingview.com"
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(index)}
                      onMouseLeave={() => handleImageMouseLeave(index)}
                    >
                      <img
                        style={imgStyle}
                        src={section.screenshotLink}
                        alt={`${section.timeframeName} Screenshot`}
                        height="auto"
                      />
                      {showDeleteIcon === index && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot(index);
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        updateSection(index, "screenshotLink", "")
                      }
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minWidth: section.description ? "80%" : "100%",
                      minHeight: "60px",
                      padding: "16px",
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      name="Screenshot Link"
                      size="small"
                      rows={calculateRows(section.screenshotLink)}
                      variant="standard"
                      value={section.screenshotLink}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        updateSection(index, "screenshotLink", e.target.value);
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}

                {/* DXY Screenshot Section - Only for USD-related pairs */}
                {isUSDRelatedPair(pair) && section.timeframeName && (
                  <>
                    <div
                      style={{
                        cursor: "default",
                        padding: "10px 16px",
                        margin: "1.5em auto 0.3em auto",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `2px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                        textAlign: "center",
                        maxWidth: "160px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#ED9E0E",
                          margin: 0,
                        }}
                      >
                        DXY {section.timeframeName}
                      </p>
                    </div>
                    {section.dxyScreenshotLink ? (
                      section.dxyScreenshotLink.startsWith(
                        "https://www.tradingview.com"
                      ) ? (
                        <div
                          style={{ position: "relative" }}
                          onMouseEnter={() => handleImageMouseEnter(index + 1000)}
                          onMouseLeave={() => handleImageMouseLeave(index + 1000)}
                        >
                          <img
                            style={imgStyle}
                            src={section.dxyScreenshotLink}
                            alt={`DXY ${section.timeframeName} Screenshot`}
                            height="auto"
                          />
                          {showDeleteIcon === index + 1000 && (
                            <Tooltip title="Delete DXY screenshot">
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteScreenshot(index, true);
                                }}
                                style={{
                                  position: "absolute",
                                  top: "8px",
                                  right: "8px",
                                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                                  borderRadius: "50%",
                                  padding: "8px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(242, 54, 69, 0.9)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(0, 0, 0, 0.7)";
                                }}
                              >
                                <DeleteIcon
                                  style={{
                                    color: "#fff",
                                    fontSize: "20px",
                                  }}
                                />
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      ) : (
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "14px",
                            color: "#f23645",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            updateSection(index, "dxyScreenshotLink", "")
                          }
                        >
                          Invalid DXY Link
                        </p>
                      )
                    ) : (
                      <div
                        style={{
                          minWidth: "100%",
                          minHeight: "60px",
                          padding: "16px",
                          borderRadius: "12px",
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(45, 46, 51, 0.4)"
                              : "rgba(249, 250, 251, 0.8)",
                          border: `1px solid ${
                            theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                          }`,
                        }}
                      >
                        <CustomMultiTextField
                          labelColor="#f23645"
                          name="DXY Screenshot Link"
                          size="small"
                          rows={calculateRows(section.dxyScreenshotLink)}
                          variant="standard"
                          value={section.dxyScreenshotLink}
                          label="No Screenshot Detected"
                          onChange={(e) => {
                            updateSection(index, "dxyScreenshotLink", e.target.value);
                          }}
                          fontSize="16px"
                          disableUnderline={true}
                        />
                      </div>
                    )}
                  </>
                )}

                <div
                  style={{
                    margin: "1.5em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color:
                          theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                      }}
                    >
                      {section.timeframeName
                        ? `${section.timeframeName} Description`
                        : "Description"}
                    </p>
                    <VoiceToText
                      onTranscript={(val) =>
                        updateSection(index, "description", val)
                      }
                      currentValue={section.description}
                    />
                  </div>
                  <CustomMultiTextField
                    name="Description"
                    size="small"
                    rows={calculateRows(section.description)}
                    variant="standard"
                    value={section.description}
                    onChange={(e) => {
                      updateSection(index, "description", e.target.value);
                    }}
                    fontSize="16px"
                    disableUnderline={true}
                  />
                </div>
                {sections.length > 2 && (
                  <div
                    onClick={() => removeSection(index)}
                    style={{
                      textAlign: "center",
                      marginTop: "8px",
                      cursor: "pointer",
                      color: "#f23645",
                      fontSize: "13px",
                      fontWeight: "600",
                      padding: "8px",
                    }}
                  >
                    REMOVE
                  </div>
                )}
              </Grid>
            ))}
            {sections.length < 7 && (
              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "200px",
                }}
              >
                <div
                  onClick={addSection}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    padding: "10px 24px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(38, 166, 93, 0.12)"
                        : "rgba(38, 166, 93, 0.08)",
                    border: `2px dashed ${
                      theme[0] === "dark" ? "#26a65d" : "#26a65d"
                    }`,
                    color: "#26a65d",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      theme[0] === "dark"
                        ? "rgba(38, 166, 93, 0.2)"
                        : "rgba(38, 166, 93, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      theme[0] === "dark"
                        ? "rgba(38, 166, 93, 0.12)"
                        : "rgba(38, 166, 93, 0.08)";
                  }}
                >
                  + Add Timeframe Section
                </div>
              </Grid>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "2em 0 1em 0",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Tooltip title="Close">
              <div
                onClick={() => goBack()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 18px",
                  borderRadius: "24px",
                  cursor: "pointer",
                  backgroundColor:
                    theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.12)"
                      : "rgba(107, 114, 128, 0.08)",
                  border: `2px solid ${
                    theme[0] === "dark" ? "#9ca3af" : "#6b7280"
                  }`,
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.backgroundColor =
                    theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.2)"
                      : "rgba(107, 114, 128, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.backgroundColor =
                    theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.12)"
                      : "rgba(107, 114, 128, 0.08)";
                }}
              >
                <CloseIcon
                  sx={{
                    color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                    fontSize: "24px",
                  }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                    color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                    display: width < 768 ? "none" : "inline",
                  }}
                >
                  CLOSE
                </span>
              </div>
            </Tooltip>

            <Tooltip title="Save Forecast">
              <div
                onClick={() => {
                  if (!formSubmitted) {
                    handleSave();
                  }
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 18px",
                  borderRadius: "24px",
                  cursor: formSubmitted ? "not-allowed" : "pointer",
                  backgroundColor: formSubmitted
                    ? theme[0] === "dark"
                      ? "rgba(107, 114, 128, 0.12)"
                      : "rgba(156, 163, 175, 0.2)"
                    : theme[0] === "dark"
                      ? "rgba(38, 166, 93, 0.12)"
                      : "rgba(38, 166, 93, 0.08)",
                  border: `2px solid ${
                    formSubmitted
                      ? theme[0] === "dark"
                        ? "#6b7280"
                        : "#9ca3af"
                      : "#26a65d"
                  }`,
                  transition: "all 0.3s ease",
                  opacity: formSubmitted ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!formSubmitted) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.backgroundColor =
                      theme[0] === "dark"
                        ? "rgba(38, 166, 93, 0.2)"
                        : "rgba(38, 166, 93, 0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!formSubmitted) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.backgroundColor =
                      theme[0] === "dark"
                        ? "rgba(38, 166, 93, 0.12)"
                        : "rgba(38, 166, 93, 0.08)";
                  }
                }}
              >
                <SaveIcon
                  sx={{
                    color: formSubmitted
                      ? theme[0] !== "dark"
                        ? "#9ca3af"
                        : "#6b7280"
                      : "#26a65d",
                    fontSize: "24px",
                  }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                    color: formSubmitted
                      ? theme[0] !== "dark"
                        ? "#9ca3af"
                        : "#6b7280"
                      : "#26a65d",
                    display: width < 768 ? "none" : "inline",
                  }}
                >
                  {formSubmitted ? "SAVING..." : "SAVE"}
                </span>
              </div>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDxyDeleteIndex(null);
        }}
        onConfirm={confirmDeleteScreenshot}
        title={dxyDeleteIndex ? "Delete DXY Screenshot" : "Delete Screenshot"}
        message={dxyDeleteIndex ? "Are you sure you want to delete this DXY screenshot?" : "Are you sure you want to delete this screenshot?"}
      />
      </>
    </Modal>
  );
};

export default NewForecast;
